/**
 * Chat Page - Wrapped in AppLayout for Admin/Master or Standalone
 */
import React, { useEffect } from 'react';
import {
  Box,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import useAuthStore from '../store/authStore';
import useChatStore from '../store/chatStore';
import Sidebar from '../components/Sidebar';
import MessageList from '../components/MessageList';
import MessageInput from '../components/MessageInput';

const DRAWER_WIDTH = 280;

export default function ChatPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { user } = useAuthStore();
  const { loadSessions, cleanup } = useChatStore();

  const [mobileOpen, setMobileOpen] = React.useState(false);

  useEffect(() => {
    // Load sessions on mount
    loadSessions();

    // Cleanup on unmount
    return () => {
      cleanup();
    };
  }, [loadSessions, cleanup]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      height: '100%', 
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Chat Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          <Sidebar onClose={handleDrawerToggle} />
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              position: 'relative',
              border: 'none',
              borderRight: '1px solid rgba(0, 0, 0, 0.08)',
            },
          }}
          open
        >
          <Sidebar />
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <MessageList />
        <MessageInput />
      </Box>
    </Box>
  );
}

